export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  // Accept both YYYY-MM-DD and full ISO timestamps
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function fmtNum(n: number | null | undefined): string {
  if (n == null) return "—";
  return new Intl.NumberFormat().format(n);
}

const compactFmt = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});

/** Compact form for chart axis ticks: 12 345 → "12.3K". */
export function fmtCompact(n: number): string {
  return compactFmt.format(n);
}

export function fmtMonth(m: string): string {
  // "YYYY-MM" → "Jan 2026"
  if (!/^\d{4}-\d{2}$/.test(m)) return m;
  const d = new Date(`${m}-01T00:00:00Z`);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** Format an over-time bucket key for a chart axis: "YYYY-MM-DD" (day) via
 *  fmtDate, "YYYY-MM" (month) via fmtMonth, "YYYY" (year) as-is. */
export function fmtPeriod(period: string, bucket: "day" | "month" | "year"): string {
  if (bucket === "day") return fmtDate(period);
  if (bucket === "year") return period;
  return fmtMonth(period);
}

/** Returns today minus `n` days as a YYYY-MM-DD string. */
export function daysAgoIso(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export const STATE_NAMES: Record<string, string> = {
  AK: "Alaska", AL: "Alabama", AR: "Arkansas", AZ: "Arizona", CA: "California",
  CO: "Colorado", CT: "Connecticut", DC: "District of Columbia", DE: "Delaware",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", IA: "Iowa", ID: "Idaho",
  IL: "Illinois", IN: "Indiana", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  MA: "Massachusetts", MD: "Maryland", ME: "Maine", MI: "Michigan",
  MN: "Minnesota", MO: "Missouri", MS: "Mississippi", MT: "Montana",
  NC: "North Carolina", ND: "North Dakota", NE: "Nebraska", NH: "New Hampshire",
  NJ: "New Jersey", NM: "New Mexico", NV: "Nevada", NY: "New York", OH: "Ohio",
  OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island",
  SC: "South Carolina", SD: "South Dakota", TN: "Tennessee", TX: "Texas",
  UT: "Utah", VA: "Virginia", VT: "Vermont", WA: "Washington", WI: "Wisconsin",
  WV: "West Virginia", WY: "Wyoming",
};

export const US_STATES = Object.keys(STATE_NAMES);

/** Full state name for a 2-letter code, or the code itself if unknown. */
export function stateName(code: string | null | undefined): string {
  if (!code) return "";
  return STATE_NAMES[code.toUpperCase()] ?? code;
}

/** "today", "3 days ago", "in 12 days" for a YYYY-MM-DD date (UTC days). */
export function fmtRelativeDays(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const then = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(then)) return null;
  const today = Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);
  const days = Math.round((then - today) / 86_400_000);
  if (days === 0) return "today";
  const n = Math.abs(days);
  const unit = n === 1 ? "day" : "days";
  return days < 0 ? `${fmtNum(n)} ${unit} ago` : `in ${fmtNum(n)} ${unit}`;
}

// enrichment_source → human label. Confidence alone is misleading: a "Web"
// row is usually just a website found by the LLM, while "Provider" is a full record.
export const SOURCE_LABEL: Record<string, string> = {
  provider: "Provider",
  edgar: "SEC",
  claude: "Web",
};
